import React from 'react';
import env from '../../environment.js';

// Swatches compartidos por parcelas-profile.js, parcelas-edit-simbologia.js y
// SimbologiaLegend, para no duplicar los mismos estilos en 3 archivos.
//
// El trazo real en el mapa (buildPolylineIcons en useGeoPuntosLayer.jsx) usa
// símbolos de Google Maps, no un `border-style` CSS — por eso "dashed" y
// "dashdot" son patrones realmente distintos ahí (guión vs. guión-punto),
// pero con solo 3 valores de `border-style` (solid/dotted/dashed) no había
// forma de diferenciarlos en este swatch. Se dibuja con SVG + `stroke-dasharray`
// para poder representar las 4 combinaciones con precisión.
const LINE_DASH_PATTERNS = {
  solid: { dasharray: undefined, linecap: 'butt' },
  dashed: { dasharray: '6 3', linecap: 'butt' },
  dotted: { dasharray: '0.1 4', linecap: 'round' },
  dashdot: { dasharray: '6 3 0.1 3', linecap: 'round' },
};

export const LineSwatch = ({ style }) => {
  const { color, dashType, weight = 2 } = style;
  const { dasharray, linecap } = LINE_DASH_PATTERNS[dashType] || LINE_DASH_PATTERNS.dashed;
  return (
    <svg width="28" height="10" style={{ marginRight: 6, verticalAlign: 'middle', flexShrink: 0 }} aria-hidden="true">
      <line
        x1="0"
        y1="5"
        x2="28"
        y2="5"
        stroke={color}
        strokeWidth={dashType === 'dotted' ? weight + 1 : weight}
        strokeDasharray={dasharray}
        strokeLinecap={linecap}
      />
    </svg>
  );
};

export const AreaSwatch = ({ fill }) => (
  <span
    style={{
      display: 'inline-block',
      width: 16,
      height: 16,
      borderRadius: 3,
      backgroundColor: fill.fill,
      border: `2px solid ${fill.stroke}`,
      opacity: 0.85,
      marginRight: 6,
      verticalAlign: 'middle',
      flexShrink: 0,
    }}
  />
);

export const PointSwatch = ({ color }) => (
  <span
    style={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: color,
      border: '2px solid #fff',
      boxShadow: '0 0 0 1px #c8d1a0',
      marginRight: 6,
      verticalAlign: 'middle',
      flexShrink: 0,
    }}
  />
);

export const TextSwatch = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 16,
      borderRadius: 3,
      backgroundColor: 'rgba(255,255,255,0.92)',
      border: '1px solid #c8d1a0',
      fontSize: 10,
      fontWeight: 700,
      color: '#4A6700',
      marginRight: 6,
      verticalAlign: 'middle',
      flexShrink: 0,
    }}>
    T
  </span>
);

// Tarjeta "Simbología" — misma tarjeta usada en parcelas-profile.js, ahora
// extraída para que padron-mapas-list.js (y cualquier otra vista de mapa)
// reutilice el componente real en vez de un duplicado.
const SimbologiaLegend = ({
  legendData,
  panTo,
  coordsCenter,
  hasData,
  showToggleButton,
  show,
  onToggleShow,
  extraArea,
  extraPoint,
  showEditButton,
  onEditClick,
}) => {
  if (!hasData) return null;

  const hasPointsOrTexts = legendData.points.length > 0 || legendData.texts.length > 0 || !!extraPoint;
  const hasLines = legendData.lines.length > 0;
  const hasPolygons = legendData.polygons.length > 0 || !!extraArea;

  return (
    <div
      style={{
        border: '1px solid #c8d1a0',
        borderRadius: 8,
        padding: '12px 16px',
        backgroundColor: '#f9faf4',
        marginBottom: 8,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#4A6700', textTransform: 'uppercase', letterSpacing: 0.8 }}>Simbología</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {showToggleButton && (
            <button
              type="button"
              onClick={onToggleShow}
              className="btn btn-parcela-action btn-sm"
              title={show ? 'Ocultar simbología' : 'Mostrar simbología'}>
              <i className={`fa ${show ? 'fa-eye-slash' : 'fa-eye'} mr-2`} aria-hidden="true"></i>
              {show ? 'Ocultar' : 'Mostrar'}
            </button>
          )}
          {showEditButton && (
            <button type="button" onClick={onEditClick} className="btn btn-parcela-action btn-sm">
              <i className="fa fa-map-marker-alt mr-2" aria-hidden="true"></i> Editar Simbología
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap' }}>
        {hasPointsOrTexts && (
          <div
            style={{
              flex: 1,
              minWidth: 180,
              paddingRight: hasLines || hasPolygons ? 20 : 0,
              borderRight: hasLines || hasPolygons ? `1px solid ${env.colors.neutral_100}` : 'none',
              marginBottom: 8,
            }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#555', marginBottom: 6, textTransform: 'uppercase' }}>Puntos / Textos</div>
            {extraPoint && (
              <div onClick={extraPoint.onClick} style={{ display: 'flex', alignItems: 'center', marginBottom: 5, cursor: 'pointer' }}>
                <PointSwatch color={extraPoint.color} />
                <span style={{ fontSize: 12, color: '#333' }}>{extraPoint.label}</span>
              </div>
            )}
            {legendData.points.map((item, i) => (
              <div
                key={i}
                onClick={() => panTo({ lat: item.lat, lng: item.lng })}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 5, cursor: 'pointer' }}>
                <img src={item.config.iconUrl} alt={item.config.label} style={{ width: 20, height: 20, marginRight: 6, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#333' }}>{item.config.label}</span>
              </div>
            ))}
            {legendData.texts.map((item, i) => (
              <div
                key={i}
                onClick={() => panTo({ lat: item.lat, lng: item.lng })}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 5, cursor: 'pointer' }}>
                <TextSwatch />
                <span style={{ fontSize: 12, color: '#333' }}>{item.nombre || `Texto ${i + 1}`}</span>
              </div>
            ))}
          </div>
        )}
        {hasLines && (
          <div
            style={{
              flex: 1,
              minWidth: 180,
              paddingLeft: hasPointsOrTexts ? 20 : 0,
              paddingRight: hasPolygons ? 20 : 0,
              borderRight: hasPolygons ? `1px solid ${env.colors.neutral_100}` : 'none',
              marginBottom: 8,
            }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#555', marginBottom: 6, textTransform: 'uppercase' }}>Líneas</div>
            {legendData.lines.map((item, i) => (
              <div
                key={i}
                onClick={() => panTo(coordsCenter(item.coordinates))}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 5, cursor: 'pointer' }}>
                <LineSwatch style={item.style} />
                <span style={{ fontSize: 12, color: '#333' }}>{item.nombre || `Línea ${i + 1}`}</span>
              </div>
            ))}
          </div>
        )}
        {hasPolygons && (
          <div style={{ flex: 1, minWidth: 180, paddingLeft: hasPointsOrTexts || hasLines ? 20 : 0, marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#555', marginBottom: 6, textTransform: 'uppercase' }}>Áreas</div>
            {extraArea && (
              <div onClick={extraArea.onClick} style={{ display: 'flex', alignItems: 'center', marginBottom: 5, cursor: 'pointer' }}>
                <AreaSwatch fill={{ fill: '#f2f1e8', stroke: '#91B833' }} />
                <span style={{ fontSize: 12, color: '#333' }}>{extraArea.label}</span>
              </div>
            )}
            {legendData.polygons.map((item, i) => (
              <div
                key={i}
                onClick={() => panTo(coordsCenter(item.coordinates))}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 5, cursor: 'pointer' }}>
                <AreaSwatch fill={item.fill} />
                <span style={{ fontSize: 12, color: '#333' }}>{item.nombre || `Área ${i + 1}`}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimbologiaLegend;
